import React from 'react'
import { useSelector } from 'react-redux'

import { useUserMetadata } from '@/hooks/useUserMetadata'
import { FullScreenLoader } from '@/components/common/FullScreenLoader'
import type { RootState } from '@/store'
import { isImpersonationActive } from '@/utils/auth/impersonationSession'

interface IAppLoaderProps {
    children: React.ReactNode
}

export function AppLoader({ children }: IAppLoaderProps) {
    // NOTE (2025-10-05): Admin impersonation first‑load UX
    // ----------------------------------------------------
    // Historically, this loader blocked initial rendering for any authenticated
    // user until a fresh GET /auth/user completed. During admin‑initiated
    // impersonation, however, we already have the impersonated user from the
    // token exchange response. Blocking here created a “stuck on splash” feel
    // until a manual refresh.
    //
    // To avoid that regression, we skip the blocking gate while an
    // impersonation session is active. Metadata continues to refresh in the
    // background via existing hooks (AuthInitializer/useUserMetadata), but
    // it does not prevent the dashboard from rendering on first paint.
    //
    // See also: pages/auth/ImpersonationExchangePage (exchange + auth seeding).
    const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated)
    const { data: userMetadata, isLoading, error } = useUserMetadata()
    const impersonating = isImpersonationActive()

    // Show loader while fetching usermetadata for authenticated users
    // Skip gating when impersonating — we already have seeded auth state
    if (isAuthenticated && !impersonating && (isLoading || !userMetadata)) {
        return <FullScreenLoader />
    }

    // Show error state if usermetadata fails to load
    // During impersonation, the banner + dashboard render is preferred even if
    // a background metadata call fails; normal auth flow still surfaces error UI.
    if (isAuthenticated && !impersonating && error) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    flexDirection: 'column',
                    gap: '1rem',
                }}
            >
                <p>Failed to load user data</p>
                <button onClick={() => window.location.reload()}>Retry</button>
            </div>
        )
    }

    return <>{children}</>
}
